import { Alert } from '@mui/material'
import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom-v5-compat'
import { DriverApplicationForm } from 'shared/administration/pages/Company/DriverApplicationForm'
import { DriverTaskForms } from 'shared/administration/pages/Company/DriverTaskForms'
import {
  ASSIGNED_ONBOARDING_PATH,
  getAssignedApplication,
  getAssignedOnboarding,
  onboardingError,
  saveAssignedSection,
} from 'shared/api/driverOnboarding'
import { Button } from 'shared/components/Button/Button'
import { TextV2 } from 'shared/components/Text/TextV2'
import { TYPOGRAPHY } from 'shared/theme/typographyV2'

import { currentUserSelector } from '../../redux/selectors/users'

export function DriverAssignments() {
  const { onboardingId } = useParams<{ onboardingId?: string }>()
  const user = useSelector(currentUserSelector)
  const client = useQueryClient()
  const key = ['assigned-onboarding', user.id, onboardingId]
  const query = useQuery(
    key,
    async () => (onboardingId ? [await getAssignedApplication(onboardingId)] : getAssignedOnboarding()),
    { retry: false, refetchOnWindowFocus: false }
  )
  const [busy, setBusy] = useState(false)
  const pending = useRef(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [version, setVersion] = useState(0)
  if (query.isLoading) return <TextV2 role="status">Loading your onboarding tasks…</TextV2>
  if (query.isError || !query.data)
    return (
      <Alert severity="error" action={<Button onClick={() => query.refetch()}>Retry</Button>}>
        Unable to load your onboarding tasks.
      </Alert>
    )
  return (
    <main className="mx-auto max-w-4xl space-y-5 p-5">
      <TextV2 type={TYPOGRAPHY.header1}>My onboarding</TextV2>
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      <Button
        variant="secondary"
        isDisabled={busy}
        onClick={async () => {
          if (!window.confirm('Reload saved progress? Unsaved changes will be lost.')) return
          const result = await query.refetch()
          if (!result.isError) {
            setVersion((v) => v + 1)
            setError('')
          }
        }}
      >
        Reload saved progress
      </Button>
      {!query.data.length && <TextV2>No onboarding sections have been assigned to you.</TextV2>}
      {query.data.map((record) => {
        const save = async (kind: string, data: unknown, submit = false) => {
          if (pending.current) return false
          pending.current = true
          setBusy(true)
          setError('')
          setNotice('')
          try {
            const updated = await saveAssignedSection(record, kind, data, submit)
            await client.cancelQueries(key)
            client.setQueryData(
              key,
              query.data!.map((item) => (item.id === updated.id ? updated : item))
            )
            setNotice(submit ? 'Sent to your admin for review.' : 'Progress saved. You can return later.')
            return true
          } catch (err) {
            setError(onboardingError(err))
            return false
          } finally {
            pending.current = false
            setBusy(false)
          }
        }
        return (
          <section key={`${record.id}:${version}`} className="space-y-5">
            <TextV2 type={TYPOGRAPHY.header2}>{record.team.name}</TextV2>
            {!onboardingId && (
              <Link to={`${ASSIGNED_ONBOARDING_PATH}${record.id}/`}>Open this application</Link>
            )}
            {!record.steps.length && <TextV2>No sections are currently assigned on this application.</TextV2>}
            {record.steps.map((step) => (
              <TextV2 key={step.kind}>
                {step.label}:{' '}
                {step.assignment_completed_at
                  ? 'Reviewed / completed'
                  : step.submitted_at
                    ? 'Sent for review'
                    : step.draft_saved_at
                      ? 'Progress saved'
                      : 'Assigned'}
              </TextV2>
            ))}
            <DriverApplicationForm record={record} save={save} busy={busy} driverView />
            <DriverTaskForms record={record} save={save} busy={busy} driverView />
          </section>
        )
      })}
    </main>
  )
}
